import { Injectable } from "@angular/core";
import { PublicHttpClientService } from "./http-client.service";
import { Observable, of } from "rxjs";
import { map, tap } from "rxjs/operators";

@Injectable()
export class PublicApplicationsService {
  constructor(private http: PublicHttpClientService) {}

  public createPdvEvent(
    id,
    { category, subcategory, name, details = {}, send_mail = false, allow_duplicate = true }
  ): Observable<any> {
    const params = {
      allow_duplicate,
      event: { category, subcategory, name, details, send_mail, created_at: new Date().toISOString() },
    };
    return this.http
      .post(`/api/public/applications/${id}/create_pdv_event`, params)
      .pipe(tap((_) => console.log("PublicApplicationsService.createPdvEvent: Ok")));
  }

  public addIncodeLog(id: string | number, { message = null }): Observable<any> {
    return this.http
      .post(`/api/public/applications/${id}/add_incode_log`, { message })
      .pipe(tap((_) => console.log("ApplicationsService.addIncodeLog: Ok")));
  }

  /**
   * Structured Incode capture logs from SDK onLog ({ type, payload }).
   * Separate from add_incode_log (string events); BE persists as incode_capture_log.
   */
  public addIncodeCaptureLog(
    id: string | number,
    { type, payload }: { type: string; payload: Record<string, unknown> }
  ): Observable<unknown> {
    return this.http
      .post(`/api/public/applications/${id}/add_incode_capture_log/`, { type, payload })
      .pipe(tap((_) => console.log("PublicApplicationsService.addIncodeCaptureLog: Ok")));
  }

  public getApplication(id: string | number, serializer = undefined): Observable<any> {
    const options = {
      params: { serializer },
    };
    return this.http
      .get(`/api/public/applications/${id}`, options)
      .pipe(tap((_) => console.log("ApplicationsService.getApplication: Ok")));
  }

  public getApplicationsFromPerson(person_id, stage?, attributes?): Observable<any> {
    const params: Record<string, string> = {};
    if (stage != null) {
      params.stage = String(stage);
    }
    if (attributes != null) {
      params.attributes = String(attributes);
    }
    return this.http
      .get(`/api/persons/${person_id}/applications`, { params })
      .pipe(tap((_) => console.log("PublicApplicationsService.getApplicationsFromPerson: Ok")));
  }

  public getOnboardingAddressQuestions(app_id: any): Observable<any> {
    return this.http
      .get("/api/public/validation_questions/onboarding_address_questions", {
        params: { application_id: app_id },
      })
      .pipe(tap((_) => console.log("ApplicationsService.getOnboardingAddressQuestions: Ok")));
  }

  public cifData(id): Observable<any> {
    return this.http
      .get(`/api/public/applications/${id}/cif_data`)
      .pipe(tap((_) => console.log("ApplicationsService.cifData: Ok")));
  }

  public updateApplication({
    id,
    approved_request = undefined,
    approved_plan = undefined,
    approval_date = undefined,
    first_downpayment_date = undefined,
    full_downpayment_date = undefined,
    sales_closing_date = undefined,
    sales_closing_reason = undefined,
    deliver_date = undefined,
    stage = undefined,
    won = undefined,
    internal_user_id = undefined,
    external_user_id = undefined,
    validation_process_id = undefined,
    applicants = undefined,
    dealership_id = undefined,
    endorsement_option = undefined,
    endorsement_relation = undefined,
  }): Observable<any> {
    const params = {
      approved_request,
      approved_plan,
      approval_date,
      first_downpayment_date,
      full_downpayment_date,
      sales_closing_date,
      sales_closing_reason,
      deliver_date,
      stage,
      won,
      internal_user_id,
      external_user_id,
      validation_process_id,
      applicants_attributes: applicants,
      dealership_id,
      endorsement_option,
      endorsement_relation,
    };
    return this.http
      .put(`/api/public/applications/${id}`, params)
      .pipe(tap((_) => console.log("ApplicationsService.updateApplication: Ok")));
  }

  public create({
    contact_id,
    approved_request = undefined,
    approved_plan = undefined,
    approval_date = undefined,
    first_downpayment_date = undefined,
    full_downpayment_date = undefined,
    deliver_date = undefined,
    stage = undefined,
    won = undefined,
    validation_process_id = undefined,
    applicants = undefined,
    dealership_id = undefined,
  }): Observable<any> {
    const params = {
      approved_request,
      approved_plan,
      approval_date,
      first_downpayment_date,
      full_downpayment_date,
      deliver_date,
      stage,
      won,
      validation_process_id,
      applicants_attributes: applicants,
      dealership_id,
    };
    return this.http
      .post(`/api/public/contact/${contact_id}/applications`, params)
      .pipe(tap((_) => console.log("ApplicationsService.create: Ok")));
  }

  public flux(id: number | string, action: string, usuario_id: number | string, options?: any): Observable<any> {
    const params = {
      flux_action: action,
      options: options || "{}",
      usuario_id,
    };
    return this.http
      .post(`/api/public/applications/${id}/flux_action`, params)
      .pipe(tap((_) => console.log("ApplicationsService.flux: Ok.")));
  }

  public getApplicants(id): Observable<any> {
    return this.http
      .get(`/api/public/applications/${id}/applicants`)
      .pipe(tap((_) => console.log("ApplicationsService.get_applicants: Ok")));
  }

  public saveApplicants(application_id: number, applicants: any): Observable<any> {
    const params = {
      applicants_attributes: applicants,
    };
    return this.http
      .put(`/api/public/applications/${application_id}/save_applicants`, params)
      .pipe(tap((_) => console.log("ApplicationsService.saveApplicants: Ok")));
  }

  public sendCifInfo(
    {
      rfc,
      first_name,
      middle_name,
      first_last_name,
      second_last_name,
      tax_regime,
      fiscal_name,
      use_person_information,
      street,
      interior_number,
      exterior_number,
      postal_code,
      sublocality,
    },
    id
  ): Observable<any> {
    const params = {
      rfc,
      first_name,
      middle_name,
      first_last_name,
      second_last_name,
      tax_regime,
      fiscal_name,
      use_person_information,
      street,
      interior_number,
      exterior_number,
      postal_code,
      sublocality,
    };
    return this.http
      .post(`/api/public/applications/${id}/update_invoice`, params)
      .pipe(tap((_) => console.log("ApplicationsService.sendCifInfo: Ok")));
  }

  public getAiWarnings<
    Warning = { type: string; severity: string; message: string; title: string; alt_message?: string }
  >(application_id: number | string): Observable<Array<Warning>> {
    return this.http
      .get(`/api/public/applications/${application_id}/ai_warnings`)
      .pipe(tap((_) => console.log("ApplicationsService.getAiWarnings: Ok")));
  }

  public getDocumentalEvaluation(application_id: number) {
    return this.http
      .get(`/api/applications/${application_id}/documental_evaluation`)
      .pipe(tap((_) => console.log("ApplicationsService.getDocumentalEvaluation: Ok")));
  }
}
